import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useReducer, useContext, useState, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import parse from 'html-react-parser';
import moment from 'moment';

import { get } from 'utils/lodash';
import { memo } from 'utils/react';
import { routes } from 'router/routes';
import { useLocalStorage } from 'hooks';
import { getFooterLinks } from 'utils/home';
import { LocaleContext } from 'providers/context';
import { formatErrorMessage } from 'utils/formatter';
import { formatDynamicFields } from 'utils/dynamic-field';
import { handleTacSubmitResponse } from 'utils/self-unlock';
import { getCoachmark } from 'middleware/actions/coachmark';
import { getSelfUnlockAccess } from 'middleware/actions/self-unlock';
import { getCustomerProfile, getTacDetail } from 'utils/settings/getter';
import { changePassword, getPasswordResult, getPasswordTac, putPasswordResult, putPasswordTac } from 'middleware/actions/settings';
import {
  getChangePasswordDynamicField,
  getForceChangePasswordDynamicField,
  getForceChangePhoneNumberDynamicField,
  getPasswordSubmitPayload,
  getPasswordValidatorMapper,
} from 'utils/settings/change-password/getter';
import {
  getLogout,
  validateUsername,
  validatePassword,
  putUserValidationResponse,
  putValidatePasswordResponse,
  getCaptchaResetCredential,
  getCheckUserStateForceChangePassword,
  putGenerateTacForceChangePassword,
  getGenerateTacForceChangePassword,
  putVerifyTacForceChangePassword,
  getVerifyTacForceChangePassword,
  getCheckUserStateForceChangePhoneNumber,
  getUpdatePhoneNumber,
} from 'middleware/actions/login';
import {
  isLengthValid,
  isValidUsername,
  isValidPasswordLogin,
  isOldCriteriaPasswordLogin,
  isPasswordMatch as checkPasswordMatch,
  isPasswordMatchFormChange,
} from 'utils/validation';

import {
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  LOGIN_STEP_FINAL,
  LOGIN_STEP_INITIAL,
  DEFAULT_CONTACT_NO,
  M2U_CLASSIC_WEBSITE,
} from 'settings/constants/common';
import { TAC_STEP_FINAL, TAC_STEP_INITIAL } from 'settings/constants/tac';
import { DEFAULT_LOCALE } from 'settings/constants/language';
import {
  CHANGE_PASSWORD_FORM_FIELDS,
  CHANGE_PHONE_NUMBER_FORM_FIELDS,
  ERR_INVALID_TAC_FORCE_CHANGE_PASS,
  ERR_RESPONSE_CODE_CHANGE_PHONE_NUMBER_501,
  ERR_RESPONSE_CODE_CHANGE_PHONE_NUMBER_502,
  ERR_RESPONSE_MESSAGE_CHANGE_PHONE_NUMBER_501,
  ERR_RESPONSE_MESSAGE_CHANGE_PHONE_NUMBER_502,
  ERR_SAME_OLD_PASSWORD_FORCE_CHANGE_PASS,
  ERR_USER_BLOCKED_FORCE_CHANGE_PASS,
  ERR_USER_LOCKED_FORCE_CHANGE_PASS,
  FORCE_CHANGE_PASSWORD_FORM_FIELDS,
  HASH_CHP_FALSE_CONDITION,
  HASH_CHP_TRUE_CONDITION,
  HASH_FALSE_CONDITION,
  HASH_TRUE_CONDITION,
  MESSAGE_SUCCESS_FORCE_CHANGE_PASS,
  RESPONSE_CODE_SUCCESS_TAC_FORCE_CHANGE_PASS,
} from 'settings/constants/login';
import { SUCCESS_RESPONSE_CODE, SUCCESS_RESPONSE_CODE_TAC } from 'settings/constants/response-codes';
import { DRAWER_TYPE_ERROR, MODAL_FULLSCREEN_SIZE, SIDEBAR_VARIANT } from 'settings/constants/ui-control';

import { Link, Header } from 'components/common';
import { BasicDrawer, TacDrawer } from 'components/common/drawers';
import { ActionButton } from 'components/common/buttons';
import { DynoAlertModal } from 'components/dyno-template/modals';
import { TopNavigation, LoginFooter, LoginForm, LoginModal, SelfUnlockModal, LoginFeedbackDrawer } from 'components/login';

import LoginAlert from 'components/login/Alert';
import RenewPasswordModal from 'components/login/RenewPasswordModal';
import PasswordChecklist from 'components/settings/security/PasswordChecklist';

import HomepageLogo from 'assets/logo/m2u-logo-id.png';
import DownloadM2UApp from 'assets/logo/download-m2u.png';
import PlayStoreLogo from 'assets/logo/play-store-mini-2x.png';
import AppStoreLogo from 'assets/logo/app-store-mini-2x.png';

import './style.scss';
import DownloadM2U from 'components/login/DownloadM2U';
import ResetCredential from 'components/login/ResetCredential';
import RenewPasswordForm from 'components/reset-credential/RenewPasswordForm';
import { encrypt, generateSHA256Hash } from 'utils/crypto';
import { generateRandomFiveNumber, removeObject } from 'utils/login';
import RenewPhoneNumberModal from 'components/login/RenewPhoneNumberModal';
import { CommonModal } from 'components/common/modals';
import PropTypes from 'prop-types';
import FooterText from '../../components/login/FooterText';

const LoginPage = ({ handleTutorial, handleCloseCoachmark }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { locale, setLocale } = useContext(LocaleContext);
  const { TEMPORARY } = SIDEBAR_VARIANT;

  const {
    isUsernameValidate,
    userValidationResponse,
    isAuthenticated,
    validatePasswordResponse,
    captchaResetCredential,
    verifyCaptchaResetCredential,
    verifyTacResetCredential,
    getTacResetCredential,
    updatePasswordResetCredential,
    checkUserStateForceChangePassword,
    generateTacForceChangePassword,
    verifyTacForceChangePassword,
    checkUserStateForceChangePhoneNumber,
    updatePhoneNumber,
    currencies,
    verifiedDetail,
    emailDetail,
    isSuccess,
    resetPasswordTacRequest,
    resetPasswordTacSubmit,
    userUnlockResponse,
    customerProfile,
    passwordAccess,
    passwordResult,
    passwordTac,
  } = useSelector(
    state => ({
      isAuthenticated: state.AuthReducer.isAuthenticated,
      isUsernameValidate: state.LoginReducer.isUsernameValidate,
      userValidationResponse: state.LoginReducer.userValidationResponse,
      validatePasswordResponse: state.LoginReducer.validatePasswordResponse,
      captchaResetCredential: state.LoginReducer.captchaResetCredential,
      verifyCaptchaResetCredential: state.LoginReducer.verifyCaptchaResetCredential,
      verifyTacResetCredential: state.LoginReducer.verifyTacResetCredential,
      getTacResetCredential: state.LoginReducer.getTacResetCredential,
      updatePasswordResetCredential: state.LoginReducer.updatePasswordResetCredential,
      checkUserStateForceChangePassword: state.LoginReducer.checkUserStateForceChangePassword,
      generateTacForceChangePassword: state.LoginReducer.generateTacForceChangePassword,
      verifyTacForceChangePassword: state.LoginReducer.verifyTacForceChangePassword,
      checkUserStateForceChangePhoneNumber: state.LoginReducer.checkUserStateForceChangePhoneNumber,
      updatePhoneNumber: state.LoginReducer.updatePhoneNumber,
      currencies: state.SelfUnlockReducer.currencies,
      verifiedDetail: state.SelfUnlockReducer.verifiedDetail,
      emailDetail: state.SelfUnlockReducer.emailDetail,
      isSuccess: state.SelfUnlockReducer.isSuccess,
      resetPasswordTacRequest: state.SelfUnlockReducer.resetPasswordTacRequestResponse,
      resetPasswordTacSubmit: state.SelfUnlockReducer.resetPasswordTacSubmitResponse,
      userUnlockResponse: state.SelfUnlockReducer.userUnlockResponse,
      customerProfile: state.AuthReducer.customerProfile,
      passwordAccess: state.SettingsReducer.passwordAccess,
      passwordResult: state.SettingsReducer.passwordResult,
      passwordTac: state.SettingsReducer.passwordTac,
    }),
    shallowEqual
  );

  const initialState = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    newPasswordFormChange: '',
    confirmNewPasswordFormChange: '',
    newPasswordResetCredential: '',
    confirmNewPasswordResetCredential: '',
  };

  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    username: '',
    password: '',
    drawerType: '',
    drawerMessage: '',
    captcha: '',
    validationCaptcha: '',
    userId: '',
    codeCaptcha: '',
    captchaId: '',
    accountNumber: '',
    otpNumber: '',
    trxId: '',
    validationTrxId: '',
    validationTacTrxId: '',
    hashUserState: '',
    hashUserStateBe: '',
    hashUserStateFalse: '',
    hashUserStateChp: '',
    hashuserStateBeChp: '',
    hashUserStateFalseChp: '',
    hashPhoneBe: '',
    hashPhone: '',
    tacStepResetCredential: TAC_STEP_INITIAL,
    tacStepForceChange: TAC_STEP_INITIAL,
    isTacDrawerOpenResetCredential: false,
    isModalOpen: false,
    isSelfUnlockOpen: false,
    isDrawerOpen: false,
    isDrawerOpenFc: false,
    step: LOGIN_STEP_INITIAL,
    selfUnlock: false,
    isRedirectMainSite: false,
    isNotificationOpen: false,
    isRenewModalOpen: false,
    isForceChangePassword: false,
    isChangeMobileNo: false,
    isModalCheckImage: false,
    isModalCheckImagePn: false,
    isTacOpen: false,
    isTacOpenForceChange: false,
    isAlertOpen: false,
    isResetCredentialOpen: false,
    invalidTacFc: false,
    invalidTacLocked: false,
    invalidMaxTacFc: false,
    invalidSubmitFc: false,
    invalidSameNumber: false,
    invalidNomberNotCriteria: false,
    modalSubmitChangePhoneNumber: false,
    errorUpdatePhoneNumber: false,
    disableButtonFcp: false,
    verifyCaptchaData: {},
    verifySecondData: {},
    getDataTac: {},
    updatePasswordCredential: {},
    checkUserStateForceChangePasswordState: {},
    generateTacForceChangePasswordState: {},
    verifyTacForceChangePasswordState: {},
    checkUserStateForceChangePhoneNumberState: {},
    updatePhoneNumberState: {},
    ...initialState,
  });

  const {
    username,
    password,
    step,
    isModalOpen,
    isSelfUnlockOpen,
    isDrawerOpen,
    isDrawerOpenFc,
    selfUnlock,
    drawerMessage,
    drawerType,
    isRedirectMainSite,
    oldPassword,
    newPassword,
    newPasswordFormChange,
    confirmNewPassword,
    confirmNewPasswordFormChange,
    hashUserState,
    hashUserStateBe,
    hashUserStateFalse,
    hashUserStateChp,
    hashuserStateBeChp,
    hashUserStateFalseChp,
    hashPhoneBe,
    hashPhone,
    isNotificationOpen,
    isRenewModalOpen,
    isForceChangePassword,
    isChangeMobileNo,
    isModalCheckImage,
    isModalCheckImagePn,
    isAlertOpen,
    isTacOpen,
    isTacOpenForceChange,
    invalidTacFc,
    invalidTacLocked,
    invalidMaxTacFc,
    invalidSubmitFc,
    invalidSameNumber,
    invalidNomberNotCriteria,
    errorUpdatePhoneNumber,
    timestamp,
    isResetCredentialOpen,
    captcha,
    tacStepForceChange,
    checkUserStateForceChangePasswordState,
    generateTacForceChangePasswordState,
    verifyTacForceChangePasswordState,
    checkUserStateForceChangePhoneNumberState,
    prevPhoneNumber,
    updatePhoneNumberState,
    modalSubmitChangePhoneNumber,
    disableButtonFcp,
  } = state;

  const captchaImgLink = get(userValidationResponse, 'imageLink', '');
  const captchaCoverLink = get(userValidationResponse, 'captchaCoverLink', '');
  const captchaImage = `${process.env.REACT_APP_FILE_URL}${captchaImgLink}`;
  const captchaBackground = `${process.env.REACT_APP_FILE_URL}${captchaCoverLink}`;
  const footerLinks = getFooterLinks(locale);
  const responseCode = get(validatePasswordResponse, 'responseCode', '');
  const responseCodeGenerateTacForceChange = isEmpty(generateTacForceChangePasswordState)
    ? ''
    : generateTacForceChangePasswordState.responseCode;

  // self unlock
  const verifiedResponseCode = get(verifiedDetail, 'responseCode', '');
  const emailResponseCode = get(emailDetail, 'responseCode', '');
  const tacSubmitResponseCode = get(resetPasswordTacSubmit, 'responseCode', '');
  const userUnlockResponseCode = get(userUnlockResponse, 'responseCode', '');
  const contactNo = get(verifiedDetail, 'serviceInfoBean.mainNote6', DEFAULT_CONTACT_NO);

  const isTacSubmitSuccess = get(resetPasswordTacSubmit, 'isSuccess', '');
  const isUnlockUserSuccess = get(userUnlockResponse, 'isSuccess', '');
  const isDetailSuccess = verifiedResponseCode === SUCCESS_RESPONSE_CODE;
  const isEmailSuccess = emailResponseCode === SUCCESS_RESPONSE_CODE;
  const isSelfUnlockRoute = history.location.pathname === routes.selfUnlock;
  const isSuccessGenerateTacForceChange = responseCodeGenerateTacForceChange === RESPONSE_CODE_SUCCESS_TAC_FORCE_CHANGE_PASS;
  const tacBean = get(passwordAccess, 'tacBean', {});
  const { tacTimestamp, isTacRequestSuccess } = getTacDetail(passwordTac);
  const { userId } = getCustomerProfile(customerProfile);

  const [languagePreferance, setLanguagePreference] = useLocalStorage('language');
  const languageFromStorage = localStorage.getItem('language');
  const languageLocalStorage =
    !languageFromStorage || isEmpty(languageFromStorage.replace(/"/g, '')) ? locale : languageFromStorage.replace(/"/g, '');

  const responseUserId = isEmpty(checkUserStateForceChangePasswordState) ? '' : checkUserStateForceChangePasswordState.userId;
  const generateTacTrxId = isEmpty(generateTacForceChangePasswordState) ? '' : generateTacForceChangePasswordState.tacTrxId;
  const generateUserId = isEmpty(generateTacForceChangePasswordState) ? '' : generateTacForceChangePasswordState.userId;
  const responseUpdatePasswordForceChange = isEmpty(verifyTacForceChangePasswordState)
    ? ''
    : verifyTacForceChangePasswordState.responseCode;
  const responseUpdatePasswordMessage = isEmpty(verifyTacForceChangePasswordState) ? '' : verifyTacForceChangePasswordState.responseMessage;

  const responseCodeCheckPhoneNumber = isEmpty(updatePhoneNumberState) ? '' : updatePhoneNumberState.responseCode;
  const responseMessageCheckPhoneNumber = isEmpty(updatePhoneNumberState) ? '' : updatePhoneNumberState.responseMessage;

  const invalidTacForceChange = responseUpdatePasswordMessage === ERR_INVALID_TAC_FORCE_CHANGE_PASS;
  const invalidMaxTacUserBlocked = responseUpdatePasswordMessage === ERR_USER_BLOCKED_FORCE_CHANGE_PASS;
  const invalidUserLocked = responseUpdatePasswordMessage === ERR_USER_LOCKED_FORCE_CHANGE_PASS;
  const invalidUpdatePasswordForceChange = responseUpdatePasswordMessage === ERR_SAME_OLD_PASSWORD_FORCE_CHANGE_PASS;

  const invalidNumberSameChangePhoneNumber = responseCodeCheckPhoneNumber === ERR_RESPONSE_CODE_CHANGE_PHONE_NUMBER_502;
  const invalidNumberNotCriteriaChangePhoneNumber = responseCodeCheckPhoneNumber === ERR_RESPONSE_CODE_CHANGE_PHONE_NUMBER_501;
  const invalidNumberMsgSameChangeNumber = responseMessageCheckPhoneNumber === ERR_RESPONSE_MESSAGE_CHANGE_PHONE_NUMBER_502;
  const invalidNumberNotCriteriaMsgChangeNumber = responseMessageCheckPhoneNumber === ERR_RESPONSE_MESSAGE_CHANGE_PHONE_NUMBER_501;
  const invalidUpdatePhoneNumber = responseCodeCheckPhoneNumber !== SUCCESS_RESPONSE_CODE;

  const isSuccessUpdatePasswordForceChange =
    responseUpdatePasswordForceChange === SUCCESS_RESPONSE_CODE && responseUpdatePasswordMessage === MESSAGE_SUCCESS_FORCE_CHANGE_PASS;

  // concate userid + user allias
  const hashUserData = generateSHA256Hash(`${responseUserId}${username}`);
  const hashUserId = generateSHA256Hash(`${generateUserId}${generateTacTrxId}`);

  const checklistValidators = getPasswordValidatorMapper(newPassword);
  const checklistValidatorsForceChangePassword = getPasswordValidatorMapper(newPasswordFormChange);
  const formFields = formatDynamicFields(CHANGE_PASSWORD_FORM_FIELDS, getChangePasswordDynamicField(state, checklistValidators));
  const formFieldsFormChangePassword = formatDynamicFields(
    FORCE_CHANGE_PASSWORD_FORM_FIELDS,
    getForceChangePasswordDynamicField(state, checklistValidatorsForceChangePassword)
  );

  const formattedFieldChangePhoneNumber = formatDynamicFields(
    CHANGE_PHONE_NUMBER_FORM_FIELDS,
    getForceChangePhoneNumberDynamicField(state)
  );

  const [isPasswordMatch, setPasswordMatch] = useState(false);
  const [isPasswordMacthForceChangePassword, setPasswordMatchForceChangePassword] = useState(false);
  const [randomNumber, setRandomNumber] = useState('');
  const [randomNumberPhone, setRandomNumberPhone] = useState('');

  const notes = (
    <PasswordChecklist
      newPassword={newPassword}
      validators={checklistValidators}
      title={formatMessage({ id: 'settings.security.changePassword.newPasswordCriteria.title' })}
    />
  );

  const notesFormChange = (
    <PasswordChecklist
      newPassword={newPasswordFormChange}
      validators={checklistValidatorsForceChangePassword}
      title={formatMessage({ id: 'settings.security.changePassword.newPasswordCriteria.title' })}
    />
  );

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(MODAL_FULLSCREEN_SIZE));

  const clearDrawer = () => setState({ isDrawerOpen: false, drawerMessage: '', drawerType: '', selfUnlock: false });
  const setDrawer = (msg, type, isSelfUnlock = false, value = {}) =>
    setState({ isDrawerOpen: true, drawerMessage: formatMessage(msg, value), drawerType: type, selfUnlock: isSelfUnlock });
  const setDrawerFc = (msg, type, isSelfUnlock = false, value = {}) =>
    setState({ isDrawerOpenFc: true, drawerMessage: formatMessage(msg, value), drawerType: type, selfUnlock: isSelfUnlock });
  const setRenewDrawer = (msg, type) => setState({ isDrawerOpen: true, drawerMessage: msg, drawerType: type });

  const handleUsername = useCallback(() => {
    if (!username) {
      return;
    }

    // Clear previous message in drawer
    clearDrawer();

    if (!isValidUsername(username)) {
      setState({ isDrawerOpen: true, drawerMessage: 'login.username.errorMessages.invalid', drawerType: DRAWER_TYPE_ERROR });
      return;
    }

    if (!isLengthValid(username, USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH)) {
      setDrawer({ id: 'login.username.errorMessages.length' }, DRAWER_TYPE_ERROR);
      return;
    }

    // Validates username first before sending to saga
    if (isLengthValid(username, USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH) && isValidUsername(username)) {
      dispatch(validateUsername(username));
    }
  }, [username]);

  const handleNavigate = pageName => {
    if (pageName === 'signup') {
      history.replace(routes.registration.default);
    }
  };

  const handleChange = (name, value) => setState({ [name]: value });

  const handlePhraseCheck = () => {
    setState({ step: LOGIN_STEP_FINAL, isModalCheckImage: true, isModalCheckImagePn: true });
  };

  const handleCloseModal = (event, reason) => {
    // dont close modal if user clicks outside of modal
    if (reason === 'backdropClick') {
      return;
    }
    // Reset page & clear redux store upon closing
    clearDrawer();
    setState({
      isModalOpen: false,
      username: '',
      password: '',
      hashUserState: '',
      hashUserStateBe: '',
      hashUserStateFalse: '',
      step: LOGIN_STEP_INITIAL,
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: {},
      verifyTacForceChangePasswordState: {},
      checkUserStateForceChangePhoneNumber: {},
      checkUserStateForceChangePhoneNumberState: {},
      tacStepForceChange: TAC_STEP_INITIAL,
      isForceChangePassword: false,
      isChangeMobileNo: false,
      isModalCheckImage: false,
      isModalCheckImagePn: false,
    });
    dispatch(putUserValidationResponse({ isUsernameValidate: false, res: {} }));
    dispatch(putValidatePasswordResponse({ responseCode: '' }));

    removeObject(checkUserStateForceChangePassword);
    removeObject(generateTacForceChangePassword);
    removeObject(verifyTacForceChangePassword);
    removeObject(checkUserStateForceChangePhoneNumber);

    setRandomNumber(generateRandomFiveNumber().toString());
    setRandomNumberPhone(generateRandomFiveNumber().toString());
    setLocale(!isEmpty(languageLocalStorage) ? languageLocalStorage : locale);
  };

  const handleLogin = () => {
    if (!isEmpty(hashUserStateBe) || isEmpty(checkUserStateForceChangePassword)) {
      if (hashUserState === hashUserStateBe || hashUserStateFalse !== hashUserStateBe || isEmpty(checkUserStateForceChangePassword)) {
        handleCloseModal();
        return;
      }
    }

    if (!isEmpty(hashuserStateBeChp) || isEmpty(checkUserStateForceChangePhoneNumber)) {
      if (
        hashUserStateChp === hashuserStateBeChp ||
        hashUserStateFalseChp !== hashuserStateBeChp ||
        isEmpty(checkUserStateForceChangePhoneNumber)
      ) {
        handleCloseModal();
        return;
      }
    }

    if (!password && !isUsernameValidate) {
      return;
    }

    // Clear previous message in drawer
    clearDrawer();
    dispatch(putValidatePasswordResponse({ responseCode: '' }));

    if (!isValidPasswordLogin(password) && !isOldCriteriaPasswordLogin(password)) {
      setDrawer({ id: 'login.password.errorMessages.invalid' }, DRAWER_TYPE_ERROR);
      return;
    }

    if ((isValidPasswordLogin(password) || isOldCriteriaPasswordLogin(password)) && hashUserStateFalse === hashUserStateBe) {
      dispatch(validatePassword(password));
    }

    // to avoid default language because rerendered isAuthenticated
    setLocale(languageLocalStorage);
  };

  const handleForgotLogin = value => {
    if (!currencies.length) dispatch(getSelfUnlockAccess());
    setState({ isSelfUnlockOpen: value });
  };

  const handleResetCredential = () => {
    dispatch(getCaptchaResetCredential({}));
  };

  const handleCloseResetCredentialModal = value =>
    setState({ isResetCredentialOpen: value, trxId: '', validationTrxId: '', validationTacTrxId: '' });

  const handleReturnToClassic = () => window.location.replace(M2U_CLASSIC_WEBSITE);

  const returnToClassic = (
    <div className="classic-m2u">
      <ActionButton
        isAssetIcon
        icon="backDesktop"
        className="db btn--m2u-classic"
        title={formatMessage({ id: 'login.footerLinks.returnToClassic' })}
        onClick={handleReturnToClassic}
      />
    </div>
  );

  const handleCancelNotification = () => {
    setState({ isNotificationOpen: false });
    dispatch(getLogout(history, true));
    handleCloseModal();
  };

  const handleChangePassword = () => {
    setState({ isNotificationOpen: false, isRenewModalOpen: true });
    dispatch(changePassword({ userId: get(customerProfile, 'userId', '') }));

    // reset all input password when change password
    setState({ ...initialState });
  };

  const onRenewPasswordModalClose = (isReset = true) => {
    if (isReset) {
      setState({ ...initialState });
    }

    setState({
      isNotificationOpen: false,
      isRenewModalOpen: false,
      isForceChangePassword: false,
      isModalCheckImage: false,
      isModalCheckImagePn: false,
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: {},
      verifyTacForceChangePasswordState: {},
      checkUserStateForceChangePhoneNumber: {},
      checkUserStateForceChangePhoneNumberState: {},
    });

    dispatch(getLogout(history, true));
    handleCloseModal();
    setLocale(!isEmpty(languageLocalStorage) ? languageLocalStorage : locale);
  };

  const onForceChangePasswordModalClose = () => {
    setState({
      isForceChangePassword: false,
      isModalCheckImage: false,
      isModalCheckImagePn: false,
      username: '',
      newPasswordFormChange: '',
      confirmNewPasswordFormChange: '',
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: {},
      verifyTacForceChangePasswordState: {},
      checkUserStateForceChangePhoneNumber: {},
      checkUserStateForceChangePhoneNumberState: {},
      step: LOGIN_STEP_INITIAL,
    });

    handleCloseModal();
    setLocale(!isEmpty(languageLocalStorage) ? languageLocalStorage : locale);
  };

  const onForceChangePhoneNumberModalClose = () => {
    setState({
      isChangeMobileNo: false,
      step: LOGIN_STEP_INITIAL,
      isModalCheckImagePn: false,
      isModalCheckImage: false,
      isForceChangePassword: false,
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: {},
      verifyTacForceChangePasswordState: {},
      checkUserStateForceChangePhoneNumber: {},
      checkUserStateForceChangePhoneNumberState: {},
      newPhoneNumber: '',
      prevPhoneNumber: '',
    });

    handleCloseModal();
    setLocale(!isEmpty(languageLocalStorage) ? languageLocalStorage : locale);
  };

  const handleCloseModalSubmitChangePhoneNumber = () => {
    setState({
      isChangeMobileNo: false,
      step: LOGIN_STEP_INITIAL,
      isModalCheckImagePn: false,
      isModalCheckImage: false,
      isForceChangePassword: false,
      modalSubmitChangePhoneNumber: false,
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: {},
      verifyTacForceChangePasswordState: {},
      checkUserStateForceChangePhoneNumber: {},
      checkUserStateForceChangePhoneNumberState: {},
      newPhoneNumber: '',
      prevPhoneNumber: '',
    });

    handleCloseModal();
  };

  const handleCancleModalSubmitChangePhoneNumber = () => {
    setState({
      ...state,
      modalSubmitChangePhoneNumber: false,
      isChangeMobileNo: true,
      isModalCheckImagePn: true,
      checkUserStateForceChangePhoneNumberState: checkUserStateForceChangePhoneNumber,
    });
  };

  const handleCloseLoginAlertModal = () => {
    setState({ isNotificationOpen: false, isAlertOpen: false });
    dispatch(getLogout(history, true));
    handleCloseModal();
  };

  const handleSaveAction = () => {
    dispatch(putPasswordTac());
    return dispatch(getPasswordTac({ userId, tacBean }));
  };

  const handleRequestTacForceChange = () => {
    // clear out response before calling TAC request again
    dispatch(putGenerateTacForceChangePassword());

    // If passed validation, call TAC request
    dispatch(
      getGenerateTacForceChangePassword({
        userId: checkUserStateForceChangePasswordState.userId,
        hashUserData,
        userAlias: encrypt(username),
        locale,
      })
    );
  };

  const handleSubmit = () => {
    if (oldPassword === newPassword) {
      setDrawer({ id: 'settings.message.currentPassword' }, DRAWER_TYPE_ERROR);
      return;
    }
    handleSaveAction();
  };

  const handleSubmitForChangePassword = () =>
    setState({
      ...state,
      isForceChangePassword: false,
      isModalCheckImage: false,
      isTacOpenForceChange: true,
      isModalOpen: false,
      isChangeMobileNo: false,
    });

  const handleSubmitForceChangePhoneNumber = () => {
    dispatch(
      getUpdatePhoneNumber({
        userId: checkUserStateForceChangePhoneNumberState.userId,
        oldPhoneNumber: encrypt(prevPhoneNumber),
        newPhoneNumber: encrypt(checkUserStateForceChangePhoneNumberState.phoneNumber),
        hashUserData: generateSHA256Hash(
          `${checkUserStateForceChangePhoneNumberState.userId}${checkUserStateForceChangePhoneNumberState.hashUserData}`
        ),
        hashUserState: generateSHA256Hash(`${hashuserStateBeChp}${randomNumberPhone}false`),
        isLastPage: false,
        trxId: randomNumberPhone,
      })
    );
  };

  const handleUpdateForceChangePhoneNumber = () => {
    dispatch(
      getUpdatePhoneNumber({
        userId: checkUserStateForceChangePhoneNumberState.userId,
        oldPhoneNumber: encrypt(prevPhoneNumber),
        newPhoneNumber: encrypt(checkUserStateForceChangePhoneNumberState.phoneNumber),
        hashUserData: generateSHA256Hash(
          `${checkUserStateForceChangePhoneNumberState.userId}${checkUserStateForceChangePhoneNumberState.hashUserData}`
        ),
        hashUserState: generateSHA256Hash(`${hashuserStateBeChp}${randomNumberPhone}true`),
        isLastPage: true,
        trxId: randomNumberPhone,
      })
    );
  };

  const handleTacClose = () => setState({ ...state, isTacOpen: false, timestamp: '' });

  const handleTacCloseForceChange = () => {
    // Reset page & clear redux store upon closing
    clearDrawer();
    setState({
      isModalOpen: false,
      username: '',
      newPasswordFormChange: invalidSubmitFc || invalidTacFc ? newPasswordFormChange : '',
      confirmNewPasswordFormChange: invalidSubmitFc || invalidTacFc ? confirmNewPasswordFormChange : '',
      step: LOGIN_STEP_INITIAL,
      checkUserStateForceChangePassword: {},
      checkUserStateForceChangePasswordState: {},
      generateTacForceChangePasswordState: invalidSubmitFc || invalidTacFc ? generateTacForceChangePasswordState : {},
      verifyTacForceChangePasswordState: {},
      tacStepForceChange: invalidSubmitFc || invalidMaxTacFc || invalidTacFc || invalidTacLocked ? TAC_STEP_FINAL : TAC_STEP_INITIAL,
      isForceChangePassword: false,
      isModalCheckImage: false,
      isTacOpenForceChange: !!(invalidSubmitFc || invalidTacFc),
      invalidTacFc: false,
      invalidMaxTacFc: false,
      invalidSubmitFc: false,
      invalidSameNumber: false,
      invalidNomberNotCriteria: false,
      invalidTacLocked: false,
    });
    dispatch(putUserValidationResponse({ isUsernameValidate: false, res: {} }));
    dispatch(putValidatePasswordResponse({ responseCode: '' }));

    setRandomNumber(generateRandomFiveNumber().toString());
    setRandomNumberPhone(generateRandomFiveNumber().toString());
  };

  const handleCloseDrawerErrChangePhoneNumber = () => {
    clearDrawer();
    setState({
      ...state,
      invalidSameNumber: false,
      invalidNomberNotCriteria: false,
      invalidTacFc: false,
      invalidMaxTacFc: false,
      invalidSubmitFc: false,
      invalidTacLocked: false,
      errorUpdatePhoneNumber: false,
      modalSubmitChangePhoneNumber: false,
      isChangeMobileNo: true,
      isModalCheckImagePn: true,
    });
  };

  const handleSubmitTac = tacValue => {
    const payload = { userId, tacValue, tacResult: passwordTac, state };
    dispatch(putPasswordResult());
    dispatch(getPasswordResult(getPasswordSubmitPayload(payload)));
  };

  const handleSubmitTacForceChange = tacValue => {
    dispatch(putVerifyTacForceChangePassword());
    dispatch(
      getVerifyTacForceChangePassword({
        userId: generateTacForceChangePasswordState.userId,
        tac: tacValue,
        hashedPassword: encrypt(newPasswordFormChange),
        hashUserId,
      })
    );
  };

  // reset credential
  const handleRefreshCaptcha = () => {
    setState({ ...state, captcha: '', captchaId: '', accountNumber: '', otpNumber: '' });
    dispatch(getCaptchaResetCredential({}));
  };

  useEffect(() => isSelfUnlockRoute && handleForgotLogin(true), []);
  useEffect(() => {
    setRandomNumber(generateRandomFiveNumber().toString());
    setRandomNumberPhone(generateRandomFiveNumber().toString());

    setState({
      ...state,
      isForceChangePassword: false,
      isModalCheckImage: false,
      isModalCheckImagePn: false,
      isChangeMobileNo: false,
    });

    removeObject(checkUserStateForceChangePassword);
    removeObject(generateTacForceChangePassword);
    removeObject(verifyTacForceChangePassword);
    removeObject(checkUserStateForceChangePhoneNumber);
    removeObject(updatePhoneNumber);
  }, []);

  useEffect(() => {
    if (isUsernameValidate) {
      setState({ isModalOpen: true });
      dispatch(getCheckUserStateForceChangePassword({ username, trxId: randomNumber }));
      dispatch(getCheckUserStateForceChangePhoneNumber({ username, trxId: randomNumberPhone }));
    }
  }, [isUsernameValidate]);

  useEffect(() => {
    if (responseCode === SUCCESS_RESPONSE_CODE && !isOldCriteriaPasswordLogin(password)) {
      dispatch(getCoachmark());
    }

    if (!isAuthenticated && responseCode && responseCode !== SUCCESS_RESPONSE_CODE) {
      setDrawer({ id: `errorMessages.ERR_${responseCode}` }, DRAWER_TYPE_ERROR);
    }

    if (responseCode === SUCCESS_RESPONSE_CODE && isOldCriteriaPasswordLogin(password)) {
      setState({ isNotificationOpen: true });
    }
  }, [isAuthenticated, responseCode]);

  // self unlock
  // NOTE: this is to display error after account/cc have been verified
  useEffect(() => {
    if (!isSuccess && !isEmpty(verifiedDetail) && !isDetailSuccess) {
      setDrawer({ id: `errorMessages.ERR_${verifiedResponseCode}` }, DRAWER_TYPE_ERROR, false, { contactNo });
    }
  }, [isSuccess, verifiedDetail]);

  // NOTE:  this is to display error when send email fail
  useEffect(() => {
    if (!isSuccess && !isEmpty(emailDetail) && !isEmailSuccess) {
      setDrawer({ id: `errorMessages.ERR_${emailResponseCode}` }, DRAWER_TYPE_ERROR);
    }

    if (isSuccess && !isEmpty(emailDetail) && isEmailSuccess) {
      setDrawer({ id: `login.feedbackDrawer.usernameSentToEmail` }, '', true);
    }
  }, [isSuccess, emailDetail]);

  useEffect(() => {
    if (isTacSubmitSuccess !== '') {
      const { tacDrawerMessage, tacDrawerType, redirectMainSite } = handleTacSubmitResponse(tacSubmitResponseCode);
      setDrawer({ id: `${tacDrawerMessage}` }, tacDrawerType, !tacDrawerType);
      setState({ isRedirectMainSite: redirectMainSite });
    }
  }, [isTacSubmitSuccess, tacSubmitResponseCode]);

  useEffect(() => {
    if (!isEmpty(userUnlockResponse)) {
      if (!isUnlockUserSuccess) {
        setDrawer({ id: `errorMessages.ERR_${userUnlockResponseCode}` }, DRAWER_TYPE_ERROR);
      } else {
        setDrawer({ id: `login.feedbackDrawer.usernameUnlocked` }, '', true);
      }
    }
  }, [isUnlockUserSuccess, userUnlockResponse]);

  useEffect(() => {
    if (languagePreferance) {
      setLocale(languagePreferance);
      return;
    }
    setLanguagePreference(DEFAULT_LOCALE);
  }, [isAuthenticated]);

  useEffect(() => {
    setPasswordMatch(checkPasswordMatch(oldPassword, newPassword, confirmNewPassword, checklistValidators));
  }, [oldPassword, newPassword, confirmNewPassword, checklistValidators]);

  useEffect(() => {
    setPasswordMatchForceChangePassword(
      isPasswordMatchFormChange(newPasswordFormChange, confirmNewPasswordFormChange, checklistValidatorsForceChangePassword)
    );
  }, [newPasswordFormChange, confirmNewPasswordFormChange, checklistValidatorsForceChangePassword]);

  useEffect(() => {
    // only update timestamp if TAC request is successful
    if (isTacRequestSuccess) {
      setState({ isTacOpen: true, timestamp: tacTimestamp });
    }
  }, [isTacRequestSuccess]);

  // Listen to reload event and prompt confirmation alert to reload if TAC drawer already open
  useEffect(() => {
    if (isTacOpen) {
      window.onbeforeunload = () => true;
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [isTacOpen]);

  useEffect(() => {
    const renewResponseCode = get(passwordResult, 'responseCode', '');
    if (renewResponseCode === SUCCESS_RESPONSE_CODE) {
      setState({
        isTacOpen: false,
        isNotificationOpen: false,
        isRenewModalOpen: false,
        isAlertOpen: true,
        isModalOpen: false,
        username: '',
        password: '',
        step: LOGIN_STEP_INITIAL,
      });
      return;
    }
    if (renewResponseCode) {
      setState({ isTacOpen: false });
      const errorCode = get(passwordResult, 'responseCode', '');
      const errorRenewMesasge = parse(formatErrorMessage(formatMessage, errorCode));

      setRenewDrawer(errorRenewMesasge, DRAWER_TYPE_ERROR);
    }
  }, [passwordResult]);

  useEffect(() => {
    setState({
      ...state,
      captcha: captchaResetCredential.captcha,
      captchaId: captchaResetCredential.captchaId,
      validationCaptcha: captchaResetCredential.captcha,
    });
    if (!isEmpty(captchaResetCredential)) {
      setState({ isResetCredentialOpen: true });
    }
  }, [captchaResetCredential]);

  useEffect(() => {
    setState({ ...state, verifyCaptchaData: verifyCaptchaResetCredential });
  }, [verifyCaptchaResetCredential]);

  useEffect(() => {
    setState({ ...state, verifySecondData: verifyTacResetCredential });
  }, [verifyTacResetCredential]);

  useEffect(() => {
    setState({
      ...state,
      getDataTac: getTacResetCredential,
      validationTacTrxId: !isEmpty(getTacResetCredential) ? getTacResetCredential.tacTrxId : '',
    });
  }, [getTacResetCredential]);

  useEffect(() => {
    setState({ ...state, updatePasswordCredential: updatePasswordResetCredential });
  }, [updatePasswordResetCredential]);

  useEffect(() => {
    if (!isEmpty(checkUserStateForceChangePassword)) {
      const responseUserState = checkUserStateForceChangePassword.hashUserState;

      setState({
        ...state,
        hashUserState: generateSHA256Hash(`${checkUserStateForceChangePassword.userId}${HASH_TRUE_CONDITION}${randomNumber}`),
        hashUserStateFalse:
          responseUserState !== generateSHA256Hash(`${checkUserStateForceChangePassword.userId}${HASH_FALSE_CONDITION}${randomNumber}`)
            ? ''
            : generateSHA256Hash(`${checkUserStateForceChangePassword.userId}${HASH_FALSE_CONDITION}${randomNumber}`),
        hashUserStateBe: checkUserStateForceChangePassword.hashUserState,
      });

      if (isModalCheckImage && checkUserStateForceChangePassword.responseCode === SUCCESS_RESPONSE_CODE) {
        setState({
          ...state,
          checkUserStateForceChangePasswordState: checkUserStateForceChangePassword,
          isForceChangePassword: hashUserState === hashUserStateBe || isModalOpen === false,
          isModalOpen: !isForceChangePassword && hashUserStateFalse === hashUserStateBe,
        });
      }
    }
  }, [checkUserStateForceChangePassword, isModalCheckImage]);

  useEffect(() => {
    if (!isEmpty(generateTacForceChangePassword))
      setState({ ...state, generateTacForceChangePasswordState: generateTacForceChangePassword });
  }, [generateTacForceChangePassword]);

  useEffect(() => {
    if (isSuccessGenerateTacForceChange) setState({ ...state, tacStepForceChange: TAC_STEP_FINAL });
  }, [responseCodeGenerateTacForceChange]);

  useEffect(() => {
    if (!isEmpty(verifyTacForceChangePassword)) setState({ ...state, verifyTacForceChangePasswordState: verifyTacForceChangePassword });
  }, [verifyTacForceChangePassword]);

  useEffect(() => {
    if (!isEmpty(verifyTacForceChangePassword) && !isEmpty(responseUpdatePasswordForceChange)) {
      if (invalidTacForceChange) setState({ ...state, invalidTacFc: true, isTacOpenForceChange: false });
      if (invalidMaxTacUserBlocked) setState({ ...state, invalidMaxTacFc: true, isTacOpenForceChange: false });
      if (invalidUpdatePasswordForceChange) setState({ ...state, invalidSubmitFc: true, isTacOpenForceChange: false });
      if (invalidUserLocked) setState({ ...state, invalidTacLocked: true, isTacOpenForceChange: false });

      if (isSuccessUpdatePasswordForceChange) {
        setState({
          ...state,
          username: '',
          newPasswordFormChange: '',
          confirmNewPasswordFormChange: '',
          checkUserStateForceChangePasswordState: {},
          checkUserStateForceChangePhoneNumberState: {},
          generateTacForceChangePasswordState: {},
          verifyTacForceChangePasswordState: {},
          tacStepForceChange: TAC_STEP_INITIAL,
          step: LOGIN_STEP_INITIAL,
          isForceChangePassword: false,
          isChangeMobileNo: false,
          isModalCheckImagePn: false,
          isModalCheckImage: false,
          isTacOpenForceChange: false,
        });
        setDrawerFc({ id: `login.forceChangePassword.tac.descriptionFinal` }, '', true);
        dispatch(putUserValidationResponse({ isUsernameValidate: false, res: {} }));

        removeObject(checkUserStateForceChangePassword);
        removeObject(generateTacForceChangePassword);
        removeObject(verifyTacForceChangePassword);
        removeObject(checkUserStateForceChangePhoneNumber);

        setRandomNumber(generateRandomFiveNumber().toString());
        setRandomNumberPhone(generateRandomFiveNumber().toString());
      }
    }
  }, [verifyTacForceChangePasswordState]);

  useEffect(() => {
    if (!isEmpty(checkUserStateForceChangePhoneNumber)) {
      const responseuserStateChp = checkUserStateForceChangePhoneNumber.hashUserState;

      setState({
        hashUserStateChp: generateSHA256Hash(
          `${checkUserStateForceChangePhoneNumber.userId}${HASH_CHP_TRUE_CONDITION}${randomNumberPhone}`
        ),
        hashUserStateFalseChp:
          responseuserStateChp !==
          generateSHA256Hash(`${checkUserStateForceChangePhoneNumber.userId}${HASH_CHP_FALSE_CONDITION}${randomNumberPhone}`)
            ? ''
            : generateSHA256Hash(`${checkUserStateForceChangePhoneNumber.userId}${HASH_CHP_FALSE_CONDITION}${randomNumberPhone}`),
        hashuserStateBeChp: checkUserStateForceChangePhoneNumber.hashUserState,
      });

      if (isModalCheckImagePn && checkUserStateForceChangePhoneNumber.responseCode === SUCCESS_RESPONSE_CODE) {
        setState({
          checkUserStateForceChangePhoneNumberState: checkUserStateForceChangePhoneNumber,
          isChangeMobileNo: hashUserStateChp === hashuserStateBeChp || isModalOpen === false,
          isModalOpen: !isChangeMobileNo && hashUserStateFalseChp === hashuserStateBeChp,
          newPhoneNumber: checkUserStateForceChangePhoneNumber.phoneNumberMask,
        });
      }
    }
  }, [checkUserStateForceChangePhoneNumber, isModalCheckImagePn]);

  useEffect(() => {
    if (!isEmpty(updatePhoneNumber)) setState({ ...state, updatePhoneNumberState: updatePhoneNumber });
  }, [updatePhoneNumber]);

  useEffect(() => {
    if (!isEmpty(updatePhoneNumber) && !isEmpty(responseCodeCheckPhoneNumber)) {
      if (invalidNumberSameChangePhoneNumber && invalidNumberMsgSameChangeNumber) setState({ ...state, invalidSameNumber: true });
      if (invalidNumberNotCriteriaChangePhoneNumber && invalidNumberNotCriteriaMsgChangeNumber)
        setState({ ...state, invalidNomberNotCriteria: true });
      if (invalidUpdatePhoneNumber && modalSubmitChangePhoneNumber) setState({ ...state, errorUpdatePhoneNumber: true });

      if (responseCodeCheckPhoneNumber === SUCCESS_RESPONSE_CODE && isChangeMobileNo) {
        setState({
          isChangeMobileNo: false,
          modalSubmitChangePhoneNumber: !modalSubmitChangePhoneNumber,
          hashPhone: generateSHA256Hash(`${prevPhoneNumber}${checkUserStateForceChangePhoneNumber.phoneNumber}|${SUCCESS_RESPONSE_CODE}`),
          hashPhoneBe: updatePhoneNumber.hashPhone,
        });
      }

      if (responseCodeCheckPhoneNumber === SUCCESS_RESPONSE_CODE && !isChangeMobileNo) {
        setState({
          ...state,
          username: '',
          newPasswordFormChange: '',
          confirmNewPasswordFormChange: '',
          newPhoneNumber: '',
          prevPhoneNumber: '',
          checkUserStateForceChangePhoneNumberState: {},
          checkUserStateForceChangePasswordState: {},
          generateTacForceChangePasswordState: {},
          verifyTacForceChangePasswordState: {},
          tacStepForceChange: TAC_STEP_INITIAL,
          step: LOGIN_STEP_INITIAL,
          modalSubmitChangePhoneNumber: !modalSubmitChangePhoneNumber,
          isChangeMobileNo: false,
          isForceChangePassword: false,
          isModalCheckImage: false,
          isModalCheckImagePn: false,
          isTacOpenForceChange: false,
          hashPhone: generateSHA256Hash(`${prevPhoneNumber}${checkUserStateForceChangePhoneNumber.phoneNumber}|${SUCCESS_RESPONSE_CODE}`),
          hashPhoneBe: updatePhoneNumber.hashPhone,
        });

        if (modalSubmitChangePhoneNumber) setDrawerFc({ id: `login.changePhoneNumber.successDesc` }, '', true);
        dispatch(putUserValidationResponse({ isUsernameValidate: false, res: {} }));

        removeObject(checkUserStateForceChangePassword);
        removeObject(generateTacForceChangePassword);
        removeObject(verifyTacForceChangePassword);
        removeObject(checkUserStateForceChangePhoneNumber);
        removeObject(updatePhoneNumberState);

        setRandomNumber(generateRandomFiveNumber().toString());
        setRandomNumberPhone(generateRandomFiveNumber().toString());
      }
    }
  }, [updatePhoneNumberState]);

  useEffect(() => {
    if (isEmpty(prevPhoneNumber)) setState({ ...state, disableButtonFcp: true });
    else setState({ ...state, disableButtonFcp: false });
  }, [prevPhoneNumber]);

  useEffect(() => {
    if (hashPhone !== hashPhoneBe && responseCodeCheckPhoneNumber === SUCCESS_RESPONSE_CODE)
      setState({ modalSubmitChangePhoneNumber: false });
  }, [hashPhone, hashPhoneBe]);

  return (
    <div className="home">
      <Header className="home--header no-bg" logo={HomepageLogo} logoLink="/" links={<TopNavigation onNavigate={handleNavigate} />} />

      <h1 className="tc home--intro">
        <FormattedMessage id="login.intro" />
      </h1>

      <LoginForm value={username} onChange={handleChange} onClick={handleUsername} />

      <Grid container>
        <Grid item xs={12} sm={12} md={12}>
          <Grid container direction="row" justify="center">
            <Link
              label={formatMessage({ id: 'login.forgotLogin' })}
              className="link--forgot-login mh3"
              hasRightIcon
              icon="keyboardArrowRight"
              onClick={() => handleForgotLogin(true)}
            />
            <Link
              label={formatMessage({ id: 'login.resetCredential' })}
              className="link--forgot-login"
              hasRightIcon
              icon="keyboardArrowRight"
              onClick={() => handleResetCredential()}
            />
          </Grid>
        </Grid>
      </Grid>

      <DownloadM2U imgDownloadM2U={DownloadM2UApp} imgAppStore={AppStoreLogo} imgPlayStore={PlayStoreLogo} />

      <FooterText />

      <LoginModal
        id="login"
        currentStep={step}
        username={username}
        password={password}
        isOpen={isModalOpen}
        avatar={captchaImage}
        cover={captchaBackground}
        onLogin={handleLogin}
        onChange={handleChange}
        onClose={handleCloseModal}
        onForgotLogin={handleForgotLogin}
        onPhraseChecking={handlePhraseCheck}
      />

      <LoginFooter footerLinks={footerLinks} returnToClassic={returnToClassic} />

      <SelfUnlockModal
        isOpen={isSelfUnlockOpen}
        onChange={handleForgotLogin}
        setDrawer={setDrawer}
        clearDrawer={clearDrawer}
        verifiedDetail={verifiedDetail}
        resetPasswordTacRequest={resetPasswordTacRequest}
        resetPasswordTacSubmit={resetPasswordTacSubmit}
        handleCloseCoachmark={handleCloseCoachmark}
        handleTutorial={handleTutorial}
      />

      <ResetCredential
        state={state}
        captcha={captcha}
        isOpen={isResetCredentialOpen}
        setDrawer={setDrawer}
        onChange={setState}
        onRefresh={handleRefreshCaptcha}
        onClose={handleCloseResetCredentialModal}
        locale={locale}
      />

      <LoginFeedbackDrawer
        isDrawerOpen={isDrawerOpen}
        drawerType={drawerType}
        drawerMessage={drawerMessage}
        handleChange={handleChange}
        selfUnlock={selfUnlock}
        isRedirectMainSite={isRedirectMainSite}
        formChangePassword={isDrawerOpenFc}
      />

      <LoginFeedbackDrawer
        isDrawerOpen={false}
        isDrawerOpenFc={isDrawerOpenFc}
        drawerType={drawerType}
        drawerMessage={drawerMessage}
        handleChange={handleChange}
        selfUnlock={selfUnlock}
        isRedirectMainSite={isRedirectMainSite}
        formChangePassword
      />

      <DynoAlertModal
        isOpen={isNotificationOpen}
        onClose={handleCancelNotification}
        onCancel={handleCancelNotification}
        onSubmit={handleChangePassword}
        hasCancelButton
        title={formatMessage({ id: 'login.oldCriteriaAlert.title' })}
        description={formatMessage({ id: 'login.oldCriteriaAlert.description' })}
        actionButtonTitle={formatMessage({ id: 'buttonText.continue' })}
      />

      <RenewPasswordModal
        fullScreen={fullScreen}
        onSubmit={handleSubmit}
        isOpen={isRenewModalOpen}
        onClose={onRenewPasswordModalClose}
        title="login.renewPasswordAlert.title"
        formDetail={state}
        onChange={setState}
        fields={formFields}
        hasNoteContent
        notes={notes}
        isRenewPassword
        isPasswordMatch={isPasswordMatch}
        actionButtonTitle="buttonText.continue"
      />

      <RenewPasswordForm
        fullScreen={fullScreen}
        onSubmit={handleSubmitForChangePassword}
        isOpen={isForceChangePassword && isModalCheckImage}
        onClose={onForceChangePasswordModalClose}
        title="login.forceChangePassword.updatePassword"
        formDetail={state}
        onChange={setState}
        fields={formFieldsFormChangePassword}
        hasNoteContent
        notes={notesFormChange}
        isRenewPassword
        isPasswordMatch={isPasswordMacthForceChangePassword}
        actionButtonTitle="buttonText.nextFc"
        formChangePasswordTitle
      />

      <RenewPhoneNumberModal
        fullScreen={fullScreen}
        onSubmit={handleSubmitForceChangePhoneNumber}
        isOpen={isChangeMobileNo && isModalCheckImagePn}
        onClose={onForceChangePhoneNumberModalClose}
        title="login.changePhoneNumber.updatePhoneNumber"
        formDetail={state}
        onChange={setState}
        fields={formattedFieldChangePhoneNumber}
        disabledButton={disableButtonFcp}
        actionButtonTitle="login.changePhoneNumber.button.next"
      />

      <LoginAlert id="loginAlert" isOpen={isAlertOpen} cover={captchaBackground} onClose={handleCloseLoginAlertModal} />

      <CommonModal
        open={modalSubmitChangePhoneNumber}
        hasCancelButton
        onCancel={handleCancleModalSubmitChangePhoneNumber}
        onSubmit={handleUpdateForceChangePhoneNumber}
        onClose={handleCloseModalSubmitChangePhoneNumber}
        title={formatMessage({ id: 'login.changePhoneNumber.modal.title' })}
        description={formatMessage({ id: 'login.changePhoneNumber.modal.description' })}
        actionButtonTitle={formatMessage({ id: 'login.changePhoneNumber.button.yes' })}
        classNameDesc="pl4"
        className="common-modal-chp"
        secondaryActionButtonTitle={formatMessage({ id: 'login.changePhoneNumber.button.no' })}
      />

      {isTacOpen && (
        <TacDrawer
          isOpen={isTacOpen}
          variant={TEMPORARY}
          hideBackdrop={false}
          timestamp={timestamp}
          onClose={handleTacClose}
          currentStep={TAC_STEP_FINAL}
          onSubmitTac={handleSubmitTac}
          onRequestTac={handleSaveAction}
          title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
          description={parse(formatMessage({ id: 'transaction.transfer.tacDescription' }))}
        />
      )}

      {isTacOpenForceChange && (
        <TacDrawer
          isOpen={isTacOpenForceChange}
          variant={TEMPORARY}
          hideBackdrop={false}
          timestamp={`${moment(new Date()).tz('Asia/Jakarta').format('Do MMMM YYYY HH:mm:ss')} GMT +7`}
          onClose={handleTacCloseForceChange}
          currentStep={tacStepForceChange}
          onSubmitTac={tacValue => handleSubmitTacForceChange(tacValue)}
          onRequestTac={handleRequestTacForceChange}
          title={formatMessage({ id: 'transaction.transfer.tacTitleFc' })}
          description={parse(formatMessage({ id: 'transaction.transfer.tacDescription' }))}
        />
      )}

      {(invalidTacFc || invalidMaxTacFc || invalidSubmitFc || invalidTacLocked) && (
        <BasicDrawer
          className="error"
          isOpen={invalidTacFc || invalidMaxTacFc || invalidSubmitFc || invalidTacLocked}
          onClose={handleTacCloseForceChange}
          hideBackDropWoModal
          hideBackdrop={false}
        >
          <h1 className="white f5 lh-copy mb0 tc">
            {invalidTacFc && <FormattedMessage id="login.forceChangePassword.errorMessage.invalidTac" />}
            {invalidMaxTacFc && <FormattedMessage id="login.forceChangePassword.errorMessage.userBlocked" />}
            {invalidSubmitFc && <FormattedMessage id="login.forceChangePassword.errorMessage.invalidOldPassword" />}
            {invalidTacLocked && <FormattedMessage id="login.forceChangePassword.errorMessage.invalidLockedUser" />}
          </h1>
        </BasicDrawer>
      )}

      {(invalidSameNumber || invalidNomberNotCriteria || errorUpdatePhoneNumber) && (
        <BasicDrawer
          className="error"
          isOpen={invalidSameNumber || invalidNomberNotCriteria || errorUpdatePhoneNumber}
          onClose={handleCloseDrawerErrChangePhoneNumber}
          hideBackdrop={false}
        >
          <h1 className="white f5 lh-copy mb0 tc">
            {invalidSameNumber && <FormattedMessage id="login.changePhoneNumber.error.sameNumber" />}
            {invalidNomberNotCriteria && <FormattedMessage id="login.changePhoneNumber.error.numberNotSame" />}
            {errorUpdatePhoneNumber && <FormattedMessage id="login.changePhoneNumber.error.errorUpdate" />}
          </h1>
        </BasicDrawer>
      )}
    </div>
  );
};

LoginPage.defaultProps = { handleTutorial: null, handleCloseCoachmark: null };

LoginPage.propTypes = { handleTutorial: PropTypes.func, handleCloseCoachmark: PropTypes.func };

export default memo(LoginPage);
